import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../contexts/FamilyContext'
import { useAuth } from '../contexts/AuthContext'
import Layout from '../components/layout/Layout'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import RewardCard from '../components/rewards/RewardCard'
import Badge from '../components/ui/Badge'
import CreateRewardModal from '../components/rewards/CreateRewardModal'
import EditRewardModal from '../components/rewards/EditRewardModal'
import DeleteRewardModal from '../components/rewards/DeleteRewardModal'
import RewardTemplateModal from '../components/rewards/RewardTemplateModal'
import QuickRewardSuggestions from '../components/rewards/QuickRewardSuggestions'

const { FiPlus, FiGift, FiStar, FiBookOpen, FiZap, FiFilter, FiSearch, FiBarChart3 } = FiIcons

const RewardsPage = () => {
  const { rewards, createReward, getChildPoints, loading } = useFamily()
  const { family } = useAuth()
  const [showCreateReward, setShowCreateReward] = useState(false)
  const [showEditReward, setShowEditReward] = useState(false)
  const [showDeleteReward, setShowDeleteReward] = useState(false)
  const [showRewardTemplates, setShowRewardTemplates] = useState(false)
  const [selectedReward, setSelectedReward] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(rewards.length === 0)

  const handleEditReward = (reward) => {
    setSelectedReward(reward)
    setShowEditReward(true)
  }

  const handleDeleteReward = (reward) => {
    setSelectedReward(reward)
    setShowDeleteReward(true)
  }

  const addSampleReward = async (reward) => {
    setFormLoading(true)
    try {
      await createReward({
        title: reward.title,
        emoji: reward.emoji,
        points_required: reward.points,
        description: `Earn ${reward.points} points to unlock this reward!`
      })
    } catch (error) {
      console.error('Error adding sample reward:', error)
    } finally {
      setFormLoading(false)
    }
  }

  // Filter rewards
  const filteredRewards = rewards.filter(reward => {
    const matchesSearch = reward.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reward.description?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = (() => {
      if (filter === 'all') return true
      if (filter === 'quick') return reward.points_required <= 40
      if (filter === 'medium') return reward.points_required > 40 && reward.points_required <= 80
      if (filter === 'big') return reward.points_required > 80 && reward.points_required <= 150
      if (filter === 'mega') return reward.points_required > 150
      return true
    })()

    return matchesSearch && matchesFilter
  })

  const pointRangeFilters = [
    { value: 'all', label: 'All Rewards', count: rewards.length },
    { value: 'quick', label: 'Quick (≤40 pts)', count: rewards.filter(r => r.points_required <= 40).length },
    { value: 'medium', label: 'Medium (41-80 pts)', count: rewards.filter(r => r.points_required > 40 && r.points_required <= 80).length },
    { value: 'big', label: 'Big (81-150 pts)', count: rewards.filter(r => r.points_required > 80 && r.points_required <= 150).length },
    { value: 'mega', label: 'Mega (150+ pts)', count: rewards.filter(r => r.points_required > 150).length }
  ]

  // Sample rewards for quick start
  const sampleRewards = [
    { title: 'Extra Screen Time', emoji: '📱', points: 25 },
    { title: 'Choose Dinner', emoji: '🍕', points: 30 },
    { title: 'Stay Up Late', emoji: '🌙', points: 40 },
    { title: 'Movie Night Pick', emoji: '🎬', points: 35 },
    { title: 'Ice Cream Trip', emoji: '🍦', points: 50 },
    { title: 'Toy Store Visit', emoji: '🧸', points: 75 },
    { title: 'Friend Sleepover', emoji: '🏠', points: 100 },
    { title: 'Special Outing', emoji: '🎡', points: 150 }
  ]

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading rewards...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Family Rewards</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Set up exciting rewards to motivate your kids
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => window.location.href = '#/reward-tracking'}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={FiBarChart3} />
              View Analytics
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowRewardTemplates(true)}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={FiBookOpen} />
              Reward Library
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowCreateReward(true)}
            >
              <SafeIcon icon={FiPlus} />
              Create Custom Reward
            </Button>
          </div>
        </div>

        {/* Children Points Overview */}
        {family?.children && family.children.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {family.children.map((child) => {
              const points = getChildPoints(child.id)
              return (
                <Card key={child.id} className="text-center">
                  <div className="text-3xl mb-2">{child.avatar || '👦'}</div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{child.name}</h3>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    <SafeIcon icon={FiStar} className="text-yellow-500 dark:text-yellow-400" />
                    <span className="text-lg font-bold text-primary-600 dark:text-primary-400">{points}</span>
                    <span className="text-gray-600 dark:text-gray-400">points</span>
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* Show suggestions for new users or toggle */}
        {(showSuggestions || rewards.length === 0) && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {rewards.length === 0 ? 'Get Started with These Popular Rewards' : 'Reward Suggestions'}
              </h2>
              {rewards.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSuggestions(!showSuggestions)}
                >
                  {showSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
                </Button>
              )}
            </div>
            <QuickRewardSuggestions maxSuggestions={6} />
          </div>
        )}

        {/* Filters */}
        {rewards.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search rewards..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Point Range Filters */}
            <div className="flex flex-wrap gap-2 mt-4">
              {pointRangeFilters.map((filterOption) => (
                <button
                  key={filterOption.value}
                  onClick={() => setFilter(filterOption.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filter === filterOption.value
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {filterOption.label}
                  {filterOption.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      filter === filterOption.value
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                      {filterOption.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Rewards Grid */}
        {filteredRewards.length > 0 ? (
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Available Rewards</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
              {filteredRewards.map((reward, index) => (
                <motion.div
                  key={reward.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <RewardCard
                    reward={reward}
                    childPoints={family?.children?.[0] ? getChildPoints(family.children[0].id) : 0}
                    canRedeem={false}
                    showActions={true}
                    onEdit={handleEditReward}
                    onDelete={handleDeleteReward}
                  />
                </motion.div>
              ))}
            </div>
          </>
        ) : rewards.length > 0 ? (
          <div className="text-center py-16 mb-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No rewards match your filters</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Try adjusting your search or filters to find rewards.
            </p>
          </div>
        ) : (
          <div className="text-center py-16 mb-12">
            <div className="text-6xl mb-4">🎁</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No rewards yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first reward to motivate your kids!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="primary"
                onClick={() => setShowRewardTemplates(true)}
              >
                <SafeIcon icon={FiBookOpen} />
                Browse Reward Library
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCreateReward(true)}
              >
                <SafeIcon icon={FiPlus} />
                Create Custom Reward
              </Button>
            </div>
          </div>
        )}

        {/* Add toggle for suggestions if rewards exist */}
        {rewards.length > 0 && !showSuggestions && (
          <div className="mb-6 text-center">
            <Button
              variant="outline"
              onClick={() => setShowSuggestions(true)}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={FiZap} />
              Show Reward Suggestions
            </Button>
          </div>
        )}

        {/* Quick Start Sample Rewards */}
        {rewards.length === 0 && !showSuggestions && (
          <div className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Quick Start: Popular Rewards
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Click any reward below to add it to your family's reward catalog
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {sampleRewards.map((reward, index) => (
                <motion.button
                  key={index}
                  onClick={() => addSampleReward(reward)}
                  disabled={formLoading}
                  className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all duration-200 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-2xl mb-2">{reward.emoji}</div>
                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-1">
                    {reward.title}
                  </div>
                  <Badge variant="primary" size="sm">
                    {reward.points} pts
                  </Badge>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Reward Statistics */}
        {rewards.length > 0 && (
          <div className="mt-12 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Reward Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{rewards.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Rewards</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {rewards.filter(r => r.points_required <= 40).length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Quick Rewards</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {rewards.filter(r => r.points_required > 40 && r.points_required <= 80).length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Medium Rewards</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {rewards.filter(r => r.points_required > 80).length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Big Rewards</div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions Panel */}
        {rewards.length > 0 && (
          <Card className="mt-8 p-6 bg-gradient-to-r from-highlight-50 to-accent-50 dark:from-highlight-900/20 dark:to-accent-900/20">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Quick Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '#/reward-tracking'}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiBarChart3} />
                View Detailed Analytics
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRewardTemplates(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiBookOpen} />
                Browse 100+ Reward Templates
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSuggestions(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiZap} />
                Get Reward Suggestions
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateReward(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiPlus} />
                Create Custom Reward
              </Button>
            </div>
          </Card>
        )}

        {/* Modals */}
        <CreateRewardModal
          isOpen={showCreateReward}
          onClose={() => setShowCreateReward(false)}
        />

        <RewardTemplateModal
          isOpen={showRewardTemplates}
          onClose={() => setShowRewardTemplates(false)}
        />

        <EditRewardModal
          isOpen={showEditReward}
          onClose={() => {
            setShowEditReward(false)
            setSelectedReward(null)
          }}
          reward={selectedReward}
        />

        <DeleteRewardModal
          isOpen={showDeleteReward}
          onClose={() => {
            setShowDeleteReward(false)
            setSelectedReward(null)
          }}
          reward={selectedReward}
        />
      </div>
    </Layout>
  )
}

export default RewardsPage